import { PaletteOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  // adds background and border properties to all of the colour variants at creation time
  interface SimplePaletteColorOptions {
    background?: React.CSSProperties['color'];
    border?: React.CSSProperties['color'];
    darkText?: React.CSSProperties['color'];
  }

  // adds background and border properties to all of the colour variants at use time
  interface PaletteColor {
    background?: React.CSSProperties['color'];
    border?: React.CSSProperties['color'];
    darkText?: React.CSSProperties['color'];
  }
}

interface BetterhelpPalette {
  main: React.CSSProperties['color'];
  dark: React.CSSProperties['color'];
  background: React.CSSProperties['color'];
}


interface OrangePalette {
  main: React.CSSProperties['color'];
  dark: React.CSSProperties['color'];
  background: React.CSSProperties['color'];
}

interface BluePalette {
  main: React.CSSProperties['color'];
  dark: React.CSSProperties['color'];
  background: React.CSSProperties['color'];
}

interface LightBluePalette {
  main: React.CSSProperties['color'];
  dark: React.CSSProperties['color'];
  background: React.CSSProperties['color'];
}
interface YellowPalette {
  main: React.CSSProperties['color'];
  dark: React.CSSProperties['color'];
  background: React.CSSProperties['color'];
}

interface PurpleGrayPalette {
  main: React.CSSProperties['color'];
  background: React.CSSProperties['color'];
}

interface BeigePalette {
  main: React.CSSProperties['color'];
  dark: React.CSSProperties['color'];
}

declare module '@mui/material/styles' {

  /*
    This interface defines the type when using `theme.palette`
  */
  interface Palette {
    lightGold: Palette['primary'];
    primaryBackground: Palette['primary'];
    darkSlate: Palette['primary'];
    orange: OrangePalette;
    blue: BluePalette;
    lightBlue: LightBluePalette;
    yellow: YellowPalette;
    purpleGray: PurpleGrayPalette;
    betterhelp: BetterhelpPalette;
    beige: BeigePalette;
  }

  interface TypeText {
    hint?: string;
  }

  /*
    This interface defines what is passed into `createTheme()`
    tertiary is marked as optional so we don't need to update older themes still floating around
    As this is just for the purposes of creation, we'll always have access to the colors

    See libs/services/src/lib/global-theme/use-theme-styles.hook.ts for an older theme
  */
  interface PaletteOptions {
    type: 'light' | 'dark';
    lightGold: PaletteOptions['primary'];
    primaryBackground: PaletteOptions['primary'];
    darkSlate: PaletteOptions['primary'];
    orange?: OrangePalette;
    blue?: BluePalette;
    lightBlue?: LightBluePalette;
    yellow?: YellowPalette;
    purpleGray?: PurpleGrayPalette;
    betterhelp?: BetterhelpPalette;
    beige?: BeigePalette;
    // hint?: PaletteOptions['text'];
  }
}

export const palette: PaletteOptions = {
  type: 'light',
  primary: {
    main: '#397A4A',
    light: '#A6DE9B',
    dark: '#325343',
    contrastText: '#FFF',
    background: '#F5FBF4',
    border: '#DEF6D9',
  },
  secondary: {
    main: '#397A4A',
    light: '#A6DE9B',
    dark: '#325343',
    contrastText: '#FFF',
    background: '#F5FBF4',
    border: '#DEF6D9',
  },
  orange: {
    main: '#A75D00',
    dark: '#6F4205',
    background: '#FFF5EE',
  },
  blue: {
    main: '#1C3FB0',
    dark: '#011556',
    background: '#EDF0FB'
  },
  lightBlue: {
    main: '#9DD5D8',
    dark: '#0E6367',
    background: '#E8FEFF'
  },
  yellow: {
    main: '#F2F957',
    dark: '#909700',
    background: '#FDFFCE',
  },
  purpleGray: {
    main: '#F1EEF4',
    background: '#F9F8FB',
  },
  info: {
    main: '#417978',
    light: '#A1DAD8',
    dark: '#205454',
    contrastText: '#FFF',
    darkText: '#205454',
    background: '#F1FBFB',
    border: '#A1DAD8',
  },
  success: {
    main: '#397A4A',
    light: '#A6DE9B',
    dark: '#325343',
    contrastText: '#212121',
    darkText: '#325343',
    background: '#F5FBF4',
    border: '#A6DE9B',
  },
  warning: {
    main: '#A75D00',
    light: '#F6BA81',
    dark: '#6F4205',
    contrastText: '#212121',
    darkText: '#6F4205',
    background: '#FFF5EE',
    border: '#F6BA81',
  },
  error: {
    main: '#C73D2D',
    light: '#F8B6B2',
    dark: '#8A2C21',
    contrastText: '#FFF',
    darkText: '#8A2C21',
    background: '#FEF4F4',
    border: '#F8B6B2',
  },
  lightGold: {
    main: '#DAD7CC',
    contrastText: '#D5D2C7',
  },
  primaryBackground: {
    main: 'rgba(72, 117, 117, 0.08)'
  },
  text: {
    primary: '#242624',
    secondary: '#4A4D4A',
    disabled: '#AEB2AC',
    hint: '#969A95'
  },
  darkSlate: {
    main: '#242624',
  },
  background: {
    paper: '#FFF',
  },
  grey: {
    A400: '#616161',
    A700: '#303030',
  },
  betterhelp: {
    main: '#7EC16A',
    dark: '#325343',
    background: '#DEF6D9',
  },
  beige: {
    main: '#FFFCF6',
    dark: '#F7F0E6',
  }
};
